import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Cpu, HardDrive, Database, Globe, Radio, MapPin, Shield, HardDriveIcon, DatabaseBackupIcon } from 'lucide-react';
import './minecraft.css';

interface PlanFeature {
  icon: React.ElementType;
  label: string;
  value: string;
}

interface PricingPlan {
  id: string;
  name: string;
  color: string;
  features: PlanFeature[];
  basePrice: number;
  popular?: boolean;
  orderLink?: string;
}

interface PricingPeriod {
  id: string;
  label: string;
  months: number;
  discount: number;
  badge?: string;
}

// Composants SVG pour les mobs Minecraft
const CreeperIcon = () => (
    <svg width="60" height="60" viewBox="0 0 116 116" fill="none" className="mob-icon">
      <g clipPath="url(#clip_creeper)">
        <path d="M49.6208 12.7075L28.0148 0L6.38051 12.7075L28.0148 25.428L49.6208 12.7075ZM11.1411 42.4621L22.5998 49.1929L27.372 52.0044L27.372 26.562L5.73642 13.8415L5.73642 39.2708L11.1411 42.4608L11.1411 42.4621ZM44.8576 42.4634L28.6576 52.0044L28.6576 26.562L50.2636 13.8415L50.2636 39.2708L44.8602 42.4608L44.8576 42.4634ZM11.4613 44.1618L21.6356 50.1384L21.6356 89.0582L11.4613 83.0685L11.4613 44.1618ZM44.5401 44.1618L44.5388 44.1625L44.5388 44.1618L44.5401 44.1618ZM44.5388 44.1625L44.5388 76.3363L33.4002 82.8787C33.3989 82.8787 33.3989 82.8799 33.3977 82.8812L22.9199 89.0582L22.9199 50.894L27.6908 53.7055L27.6934 53.7055C27.7024 53.7107 27.7152 53.7107 27.7255 53.7159C27.813 53.7605 27.9068 53.7919 28.0109 53.7919L28.0161 53.7919C28.1202 53.7919 28.2153 53.7605 28.3015 53.7159C28.3105 53.7107 28.3233 53.7107 28.3336 53.7055L28.3362 53.7055L44.5388 44.1625ZM0.642809 76.7135L10.1756 71.1036L10.1756 82.313L0.642809 76.7135ZM35.0085 83.447L45.1829 89.4366L55.3572 83.447L45.1829 77.4691L35.0085 83.447ZM10.1756 83.8254L0 77.8475L0 96.5473L10.1756 102.523L10.1756 83.8254ZM33.7229 96.1688L23.5627 90.1922L33.7229 84.2026L43.8972 90.1922L33.7229 96.1688ZM56 103.278L45.8257 109.268L45.8257 90.5707L56 84.581L56 103.278ZM21.6356 96.5473L11.4613 102.523L11.4613 84.581L21.6356 90.5707L21.6356 96.5473ZM22.9212 91.3263L22.9212 110.025L33.0801 116L33.0801 97.3029L22.9212 91.3263ZM44.5401 110.023L44.5401 91.3263L34.3657 97.3042L34.3657 116L44.5401 110.023Z"
              fill="currentColor" transform="translate(30 0)"/>
      </g>
      <defs>
        <clipPath id="clip_creeper">
          <rect width="116" height="116"/>
        </clipPath>
      </defs>
    </svg>
);

const ZombieIcon = () => (
    <svg width="60" height="60" viewBox="0 0 116 116" fill="none" className="mob-icon">
      <g clipPath="url(#clip_zombie)">
        <path d="M58 0L30 15L58 30L86 15L58 0ZM25 45L35 50V80L25 75V45ZM91 45V75L81 80V50L91 45ZM40 55V85L58 95L76 85V55L58 65L40 55ZM20 85L30 90V110L20 105V85ZM96 85V105L86 110V90L96 85ZM35 95V115L58 105L81 115V95L58 100L35 95Z"
              fill="currentColor"/>
      </g>
      <defs>
        <clipPath id="clip_zombie">
          <rect width="116" height="116"/>
        </clipPath>
      </defs>
    </svg>
);

const BlazeIcon = () => (
    <svg width="60" height="60" viewBox="0 0 116 116" fill="none" className="mob-icon">
      <g clipPath="url(#clip_blaze)">
        <path d="M58 10L48 20L58 30L68 20L58 10ZM40 25L30 35L40 45L50 35L40 25ZM76 25L86 35L76 45L66 35L76 25ZM58 40L48 50L58 60L68 50L58 40ZM25 45L15 55L25 65L35 55L25 45ZM91 45L101 55L91 65L81 55L91 45ZM40 70L30 80L40 90L50 80L40 70ZM76 70L86 80L76 90L66 80L76 70ZM58 85L48 95L58 105L68 95L58 85Z"
              fill="currentColor"/>
      </g>
      <defs>
        <clipPath id="clip_blaze">
          <rect width="116" height="116"/>
        </clipPath>
      </defs>
    </svg>
);

const SpiderIcon = () => (
    <svg width="60" height="60" viewBox="0 0 116 116" fill="none" className="mob-icon">
      <g clipPath="url(#clip_spider)">
        <path d="M58 30C70 30 80 40 80 52C80 64 70 74 58 74C46 74 36 64 36 52C36 40 46 30 58 30ZM25 45L15 35L10 40L20 50L25 45ZM91 45L96 40L106 35L101 45L91 45ZM20 52L10 47L5 52L15 57L20 52ZM96 52L101 57L111 52L106 47L96 52ZM25 59L20 64L10 74L15 79L25 69L25 59ZM91 59L91 69L101 79L106 74L96 64L91 59Z"
              fill="currentColor"/>
      </g>
      <defs>
        <clipPath id="clip_spider">
          <rect width="116" height="116"/>
        </clipPath>
      </defs>
    </svg>
);

const EnderIcon = () => (
    <svg width="60" height="60" viewBox="0 0 116 116" fill="none" className="mob-icon">
      <g clipPath="url(#clip_ender)">
        <path d="M58 5L45 15L58 25L71 15L58 5ZM35 20L22 30L35 40L48 30L35 20ZM81 20L94 30L81 40L68 30L81 20ZM58 35L45 45L58 55L71 45L58 35ZM22 50L9 60L22 70L35 60L22 50ZM94 50L107 60L94 70L81 60L94 50ZM45 65L32 75L45 85L58 75L45 65ZM71 65L84 75L71 85L58 75L71 65ZM58 95L45 105L58 115L71 105L58 95Z"
              fill="currentColor"/>
      </g>
      <defs>
        <clipPath id="clip_ender">
          <rect width="116" height="116"/>
        </clipPath>
      </defs>
    </svg>
);

const Price: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');

  const periods: PricingPeriod[] = [
    { id: 'monthly', label: 'Par mois', months: 1, discount: 0 },
    { id: '3months', label: '3 mois', months: 3, discount: 0, badge: '-5%' },
    { id: '6months', label: '6 mois', months: 6, discount: 0, badge: '-5%' },
    { id: '1year', label: '1 an', months: 12, discount: 0, badge: '-5%' }
  ];

  const plans: PricingPlan[] = [
    {
      id: 'dev',
      name: 'Minecraft | DEV',
      color: 'green',
      basePrice: 1.99,
      orderLink: 'https://client.hostware.fr/store/basket/add/1',
      features: [
        { icon: Cpu, label: 'CPU', value: '2x vCPU' },
        { icon: HardDrive, label: 'RAM', value: '6 GB Ram' },
        { icon: HardDriveIcon, label: 'Stockage', value: '12 GB Stockage' },
        { icon: DatabaseBackupIcon, label: 'Backup', value: '1' },
        { icon: Database, label: 'Base de données MySQL', value: '1' },
        { icon: Radio, label: 'Slots', value: 'Illimités' },
        { icon: Radio, label: 'Bande passante', value: '10 GB/s' },
        { icon: MapPin, label: 'Localisation', value: 'Paris, France' }
      ]
    },
    {
      id: 'starter',
      name: 'minecraft | Starter',
      color: 'blue',
      basePrice: 3.99,
      orderLink: 'https://client.hostware.fr/store/basket/add/2',
      features: [
        { icon: Cpu, label: 'CPU', value: '3x vCPU' },
        { icon: HardDrive, label: 'RAM', value: '6 GB Ram' },
        { icon: HardDriveIcon, label: 'Stockage', value: '64 GB Stockage' },
        { icon: DatabaseBackupIcon, label: 'Backup', value: '1' },
        { icon: Database, label: 'Base de données MySQL', value: '2' },
        { icon: Radio, label: 'Slots', value: 'Illimités' },
        { icon: Radio, label: 'Bande passante', value: '10 GB/s' },
        { icon: MapPin, label: 'Localisation', value: 'Paris, France' }
      ]
    },
    {
      id: 'premium',
      name: 'Minecraft | Premium',
      color: 'yellow',
      basePrice: 9.99,
      popular: true,
      orderLink: 'https://client.hostware.fr/store/basket/add/3',
      features: [
        { icon: Cpu, label: 'CPU', value: '5x vCPU' },
        { icon: HardDrive, label: 'RAM', value: '12 GB Ram' },
        { icon: HardDriveIcon, label: 'Stockage', value: '96 GB Stockage' },
        { icon: DatabaseBackupIcon, label: 'Backup', value: '1' },
        { icon: Database, label: 'Base de données MySQL', value: '3' },
        { icon: Radio, label: 'Slots', value: 'Illimités' },
        { icon: Radio, label: 'Bande passante', value: '10 GB/s' },
        { icon: MapPin, label: 'Localisation', value: 'Paris, France' }
      ]
    },
    {
      id: 'ultra',
      name: 'Minecraft | Ultra',
      color: 'red',
      basePrice: 19.99,
      orderLink: 'https://client.hostware.fr/store/basket/add/4',
      features: [
        { icon: Cpu, label: 'CPU', value: '8x vCPU' },
        { icon: HardDrive, label: 'RAM', value: '24 GB Ram' },
        { icon: HardDriveIcon, label: 'Stockage', value: '128 GB Stockage' },
        { icon: DatabaseBackupIcon, label: 'Backup', value: '1' },
        { icon: Database, label: 'Base de données MySQL', value: '4' },
        { icon: Radio, label: 'Slots', value: 'Illimités' },
        { icon: Radio, label: 'Bande passante', value: '10 GB/s' },
        { icon: MapPin, label: 'Localisation', value: 'Paris, France' }
      ]
    }
  ];

  const getMobIcon = (planId: string) => {
    switch (planId) {
      case 'creeper': return <CreeperIcon />;
      case 'zombie': return <ZombieIcon />;
      case 'blaze': return <BlazeIcon />;
      case 'spider': return <SpiderIcon />;
      case 'ender': return <EnderIcon />;
      default: return <CreeperIcon />;
    }
  };

  const calculatePrice = (basePrice: number, selectedPeriodId: string, planId: string) => {
    const period = periods.find(p => p.id === selectedPeriodId);
    if (!period) return { price: basePrice, total: basePrice, savings: 0 };

    // Appliquer 5% de réduction uniquement sur Lite et Premium pour 3m/6m/1an
    const eligible = planId === 'ultra' || planId === 'premium';
    const discount = (period.id !== 'monthly' && eligible) ? 5 : 0;

    const discountedPrice = basePrice * (1 - discount / 100);
    const totalPrice = discountedPrice * period.months;

    return {
      price: discountedPrice,
      total: totalPrice,
      savings: (period.months > 1 && discount > 0) ? (basePrice * period.months - totalPrice) : 0
    };
  };

  const getSelectedPeriod = () => periods.find(p => p.id === selectedPeriod);

  return (
      <>
        <Helmet>
          <title>HOSTWARE® – Hébergement Serveur Minecraft Puissant dès 1.99€/mois</title>
          <meta
              name="description"
              content="Louez un serveur Minecraft performant avec slots illimités, Anti-DDoS, sauvegardes automatiques et base de données MySQL incluse. Offres à partir de 1.99€/mois chez HostWare."
          />
          <meta
              name="keywords"
              content="hébergement minecraft, serveur minecraft, serveur gaming, hébergement jeu, minecraft hosting, vps gaming, hébergement pas cher, serveurs minecraft performants"
          />
          <meta
              name="title"
              content="Serveur Minecraft performant & fiable - HostWare"
          />

          {/* Open Graph / Facebook */}
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content="HostWare" />
          <meta property="og:locale" content="fr_FR" />
          <meta property="og:url" content="https://hostware.fr/minecraft" />
          <meta property="og:title" content="HOSTWARE – Hébergement Serveur Minecraft Puissant dès 1.99€/mois" />
          <meta
              property="og:description"
              content="Louez un serveur Minecraft performant avec slots illimités, Anti-DDoS, sauvegardes automatiques et base de données MySQL incluse. Offres dès 1.99€/mois."
          />
          <meta property="og:image" content="https://hostware.fr/logo512.png" />

          {/* Twitter */}
          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:title" content="HOSTWARE – Hébergement Serveur Minecraft Puissant dès 1.99€/mois" />
          <meta
              property="twitter:description"
              content="Louez un serveur Minecraft performant avec slots illimités, Anti-DDoS, sauvegardes automatiques et base de données MySQL incluse. Offres dès 1.99€/mois."
          />
          <meta property="twitter:image" content="https://hostware.fr/logo512.png" />
          <meta property="twitter:image:width" content="512" />
          <meta property="twitter:image:height" content="512" />
          <meta property="twitter:image:alt" content="Logo HostWare" />
        </Helmet>

        <div className="pricing-container">
          {/* Background Effects */}
          <div className="background-overlay"></div>
          <div className="background-orb orb-1"></div>
          <div className="background-orb orb-2"></div>
          <div className="floating-particles"></div>

          {/* Hero Section */}
          <div className="hero-section">
            <div className="hero-content">
              <div className="rocket-icon animate-bounce-slow">🚀</div>
              <h1 className="hero-title animate-fade-in-up">
                DÉCOUVREZ NOS OFFRES
              </h1>
              <p className="hero-subtitle animate-fade-in-up" style={{animationDelay: '0.2s'}}>
                Louez votre serveur Minecraft pour jouer avec vos amis ou en communauté. Profitez de performances exceptionnelles pour une expérience de jeu inoubliable.
              </p>

              {/* Duration Tabs */}
              <div className="duration-tabs animate-fade-in-up" style={{animationDelay: '0.4s'}}>
                {periods.map((period) => (
                    <button
                        key={period.id}
                        onClick={() => setSelectedPeriod(period.id)}
                        className={`duration-tab ${selectedPeriod === period.id ? 'active' : ''}`}
                    >
                      {period.label}
                      {period.badge && (
                          <span className="discount-badge animate-pulse-soft">
                      {period.badge}
                    </span>
                      )}
                    </button>
                ))}
              </div>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="pricing-section">
            <div className="plans-grid">
              {plans.map((plan, index) => {
                const pricing = calculatePrice(plan.basePrice, selectedPeriod, plan.id);
                const currentPeriod = getSelectedPeriod();

                return (
                    <div
                        key={plan.id}
                        className={`plan-card ${plan.color} ${plan.popular ? 'popular' : ''} animate-fade-in-up`}
                        style={{animationDelay: `${0.1 * index}s`}}
                    >
                      {plan.popular && (
                          <div className="popular-badge animate-glow">
                            Populaire
                          </div>
                      )}

                      {/* Plan Header */}
                      <div className="plan-header">
                        <div className="plan-icon-container">
                          {getMobIcon(plan.id)}
                        </div>
                        <h3 className="plan-name">{plan.name}</h3>
                      </div>

                      {/* Features */}
                      <div className="plan-features">
                        {plan.features.map((feature, featureIndex) => (
                            <div
                                key={featureIndex}
                                className="feature-row animate-slide-in-left"
                                style={{animationDelay: `${0.6 + featureIndex * 0.1}s`}}
                            >
                              <feature.icon className="feature-icon" size={16} />
                              <span className="feature-label">{feature.label}</span>
                              <span className="feature-value">{feature.value}</span>
                            </div>
                        ))}
                      </div>

                      {/* Anti-DDoS Badge */}
                      <div className="plan-security animate-fade-in">
                        <div className="security-badge">
                          <Shield size={14} />
                          ANTI-DDOS
                        </div>
                      </div>

                      {/* Pricing */}
                      <div className="plan-pricing">
                        <div className="price-display">
                          <div className="price animate-number-change">
                            {pricing.price.toFixed(2)} €
                          </div>
                          <div className="duration">{currentPeriod?.label}</div>
                        </div>

                        {currentPeriod && currentPeriod.months > 1 && (
                            <div className="pricing-details">
                              <div className="total-price">
                                Total: {pricing.total.toFixed(2)} €
                              </div>
                              {pricing.savings > 0 && (
                                  <div className="savings animate-pulse-soft">
                                    Économie: {pricing.savings.toFixed(2)} €
                                  </div>
                              )}
                            </div>
                        )}
                      </div>

                      {/* CTA Button */}
                      <a
                          href={plan.orderLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="plan-button animate-button-hover"
                      >
                        <span>Commander</span>
                        <div className="button-shine"></div>
                      </a>
                    </div>
                );
              })}
            </div>
          </div>
        </div>
      </>
  );
};

export default Price;